import React, { useState, useEffect, useRef } from 'react';
import { 
  Send, 
  ArrowLeft, 
  BookOpen, 
  User, 
  School, 
  GraduationCap,
  MessageSquare,
  Loader,
  AlertCircle,
  Bot,
  X,
  Edit3,
  Save,
  Clock,
  MessageCircle
} from 'lucide-react';
import openaiService from '../services/openaiService';
import SimpleVoiceChat from './SimpleVoiceChat';
import '../styles/chat-interface.css';

const ChatInterface = ({ 
  materia, 
  aluno, 
  onBack, 
  onClose 
}) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [error, setError] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [chatTitle, setChatTitle] = useState(`Chat de ${materia}`);
  const [isTyping, setIsTyping] = useState(false);
  const [isVoiceChatOpen, setIsVoiceChatOpen] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll para a última mensagem
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Inicializar chat ao montar o componente
  useEffect(() => {
    initializeChat();
  }, []);

  // Inicializar chat
  const initializeChat = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Buscar ou criar chat
      const chat = await openaiService.getOrCreateChat(
        aluno.id,
        materia,
        aluno.escolas?.nome || 'Escola não informada',
        aluno.anos_escolares?.nome || 'Ano não informado',
        aluno.periodo || 'Período não informado'
      );

      setChatId(chat.id);
      setChatTitle(chat.titulo || `Chat de ${materia}`);

      // Buscar histórico de mensagens
      const chatHistory = await openaiService.getChatHistory(chat.id);
      setMessages(chatHistory);

              // Mensagem de boas-vindas se não houver histórico
        if (chatHistory.length === 0) {
          const welcomeMessage = {
            id: 'welcome',
            tipo_mensagem: 'assistant',
            conteudo: `Olá ${aluno.nome.split(' ')[0]}! 👋 

Bem-vindo ao seu chat de ${materia}! 

Eu sou seu tutor de ${materia} e estou aqui para te ajudar a aprender de forma divertida e interativa. 

Como posso te ajudar hoje? Você pode me fazer perguntas sobre ${materia}, pedir explicações, ou até mesmo sugerir um tema para estudarmos juntos! 📚✨`,
            data_envio: new Date().toISOString()
          };
          setMessages([welcomeMessage]);
        }

    } catch (error) {
      console.error('Erro ao inicializar chat:', error);
      setError('Erro ao carregar o chat. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Enviar mensagem
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Adicionar mensagem do usuário imediatamente
    const userMsg = {
      id: `user-${Date.now()}`,
      tipo_mensagem: 'user',
      conteudo: userMessage,
      data_envio: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);

    try {
      // Enviar para OpenAI
      const response = await openaiService.sendMessage(
        chatId,
        userMessage,
        materia,
        aluno.nome,
        aluno.anos_escolares?.nome || 'Ano não informado',
        aluno.escolas?.nome || 'Escola não informada'
      );

      if (response.success) {
        // Adicionar resposta da IA
        const aiMessage = {
          id: `ai-${Date.now()}`,
          tipo_mensagem: 'assistant',
          conteudo: response.message,
          data_envio: new Date().toISOString()
        };

        setMessages(prev => [...prev, aiMessage]);
      }

    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      
      // Adicionar mensagem de erro
      const errorMessage = {
        id: `error-${Date.now()}`,
        tipo_mensagem: 'assistant',
        conteudo: 'Desculpe, tive um problema para processar sua mensagem. Tente novamente em alguns instantes.',
        data_envio: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Editar título do chat
  const handleEditTitle = async () => {
    if (isEditingTitle) {
      try {
        await openaiService.renameChat(chatId, chatTitle);
        setIsEditingTitle(false);
      } catch (error) {
        console.error('Erro ao renomear chat:', error);
        setError('Erro ao renomear o chat.');
      }
    } else {
      setIsEditingTitle(true);
    }
  };

  // Fechar chat
  const handleCloseChat = async () => {
    if (window.confirm('Tem certeza que deseja fechar este chat?')) {
      try {
        if (chatId) {
          await openaiService.closeChat(chatId);
        }
        onClose();
      } catch (error) {
        console.error('Erro ao fechar chat:', error);
        onClose(); // Fechar mesmo com erro
      }
    }
  };

  // Formatar timestamp
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formatar data
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Hoje';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ontem';
    } else {
      return date.toLocaleDateString('pt-BR');
    }
  };

  if (isLoading) {
    return (
      <div className="chat-container">
        <div className="chat-loading">
          <Loader size={48} className="loading-spinner" />
          <p>Inicializando seu chat de {materia}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      {/* Header do Chat */}
      <div className="chat-header">
        <div className="chat-header-left">
          <button className="btn-back" onClick={onBack} title="Voltar ao Dashboard">
            <ArrowLeft size={20} />
          </button>
          
          <div className="chat-info">
            <div className="chat-subject">
              <BookOpen size={16} />
              {materia}
            </div>
            
            {isEditingTitle ? (
              <div className="title-edit">
                <input
                  type="text"
                  value={chatTitle}
                  onChange={(e) => setChatTitle(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleEditTitle()}
                  autoFocus
                />
                <button onClick={handleEditTitle} title="Salvar">
                  <Save size={16} />
                </button>
              </div>
            ) : (
              <h2 className="chat-title" onClick={handleEditTitle} title="Clique para editar">
                {chatTitle}
                <Edit3 size={14} className="edit-icon" />
              </h2>
            )}
          </div>
        </div>

        <div className="chat-header-right">
          <div className="aluno-info">
            <span className="aluno-name">
              <User size={14} />
              {aluno.nome.split(' ')[0]}
            </span>
            <span className="aluno-grade">
              <GraduationCap size={14} />
              {aluno.anos_escolares?.nome || 'N/A'}
            </span>
            <span className="aluno-school">
              <School size={14} />
              {aluno.escolas?.nome || 'N/A'}
            </span>
          </div>
          
          <button 
            className="btn-voice-chat" 
            onClick={() => setIsVoiceChatOpen(true)} 
            title="Conversar com IA"
          >
            <MessageCircle size={20} />
            <span>Conversar</span>
          </button>
          
          <button className="btn-close" onClick={handleCloseChat} title="Fechar Chat">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Área de Mensagens */}
      <div className="chat-messages">
        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {messages.map((message, index) => {
          const showDate = index === 0 || 
            formatDate(messages[index - 1].data_envio) !== formatDate(message.data_envio);

          return (
            <div key={message.id}>
              {showDate && (
                <div className="message-date">
                  {formatDate(message.data_envio)}
                </div>
              )}
              
              <div className={`message ${message.tipo_mensagem}`}>
                <div className="message-avatar">
                  {message.tipo_mensagem === 'user' ? (
                    <User size={20} />
                  ) : (
                    <img 
                      src={require('./avatar-static.jpg')} 
                      alt="IA Tutor" 
                      className="ai-avatar-chat"
                      style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                    />
                  )}
                </div>
                
                <div className="message-content">
                  <div className="message-text">
                    {message.conteudo}
                  </div>
                  
                  <div className="message-time">
                    <Clock size={12} />
                    {formatTime(message.data_envio)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div className="message assistant typing">
            <div className="message-avatar">
              <img 
                src={require('./avatar-static.jpg')} 
                alt="IA Tutor" 
                className="ai-avatar-chat"
                style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
              />
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input de Mensagem */}
      <form className="chat-input-container" onSubmit={handleSendMessage}>
        <div className="chat-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder={`Digite sua pergunta sobre ${materia}...`}
            disabled={isLoading}
            className="chat-input"
          />
          
          <button
            type="submit"
            disabled={!inputMessage.trim() || isLoading}
            className="btn-send"
            title="Enviar mensagem"
          >
            {isLoading ? (
              <Loader size={18} className="spinning" />
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
        
        <div className="chat-input-info">
          <MessageSquare size={12} />
          <span>Pressione Enter para enviar</span>
        </div>
      </form>

      {/* Popup de Conversa por Voz */}
      <SimpleVoiceChat
        isOpen={isVoiceChatOpen}
        onClose={() => setIsVoiceChatOpen(false)}
        materia={materia}
        aluno={aluno}
      />
    </div>
  );
};

export default ChatInterface;
